import { z } from 'zod';
// A pay period resolves to a concrete inclusive [start_date, end_date] range of
// DATE-only days (YYYY-MM-DD, interpreted in Asia/Jerusalem). The box-level
// config decides HOW a period is anchored; the report then resolves a concrete
// range for the selected month/range.
//
// Two anchoring modes (guidelines 03 §15 — all date boundaries in
// Asia/Jerusalem):
//   - calendar_month: the 1st → last day of a calendar month.
//   - custom:         a fixed day-of-month start (e.g. 25) running to the day
//                     before that day in the next month (25→24 inclusive).
const ISO_DATE = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');
export const PayPeriodModeSchema = z.enum(['calendar_month', 'custom']);
/** Box-level configuration of how a pay period is anchored. */
export const PayPeriodConfigSchema = z.object({
    mode: PayPeriodModeSchema,
    // For 'custom': day-of-month the period starts on (1..28; capped at 28 to
    // avoid month-length ambiguity). Ignored for 'calendar_month'.
    custom_start_day: z.number().int().min(1).max(28).nullable(),
});
/** A concrete, resolved period — what the report and email/Excel operate on. */
export const ResolvedPayPeriodSchema = z.object({
    start_date: ISO_DATE,
    end_date: ISO_DATE, // inclusive
    // Human label for UI + email subject + Excel filename, e.g. "מאי 2026" or
    // "25.05.2026–24.06.2026". Built server-side so every surface agrees.
    label: z.string(),
});
/** Period selector on the report.
 *  - last/this/next_month: relative to today (used by the auto-send cron).
 *  - month: an explicit calendar month (year + 1-indexed month) — what the
 *    report UI now uses (full months only).
 *  - custom: an explicit inclusive range (kept for completeness / future use). */
export const PeriodSelectionSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('last_month') }),
    z.object({ kind: z.literal('this_month') }),
    z.object({ kind: z.literal('next_month') }),
    z.object({
        kind: z.literal('month'),
        year: z.number().int().min(2000).max(2100),
        month: z.number().int().min(1).max(12),
    }),
    z.object({ kind: z.literal('custom'), start_date: ISO_DATE, end_date: ISO_DATE }),
]);
// ── Resolver ────────────────────────────────────────────────────────────────
// Pure date arithmetic on integer (year, month, day) — no Date-timezone pitfalls.
// "today" must already be expressed in Asia/Jerusalem (see todayInJerusalem).
const HE_MONTHS = [
    'ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני',
    'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר',
];
const HE_DAYS = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
/** Hebrew weekday name for a YYYY-MM-DD date (Sun=ראשון … Sat=שבת). Derived from
 *  the date so it's reliable — Arbox's day_of_week field comes through numeric. */
export function hebrewDayOfWeek(dateISO) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateISO);
    if (!m)
        return null;
    const [, y, mo, d] = m.map(Number);
    return HE_DAYS[new Date(Date.UTC(y, mo - 1, d)).getUTCDay()] ?? null;
}
/** Format a date as DD/MM/YYYY for display. Accepts a date-only YYYY-MM-DD
 *  string (no TZ shift — the common case for meeting/period dates) or a full
 *  ISO timestamp (e.g. locked_at), which is rendered in local time. Returns the
 *  input unchanged if it can't be parsed. */
export function formatDateDDMMYYYY(input) {
    if (!input)
        return '';
    const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(input);
    if (dateOnly) {
        const [, y, mo, d] = dateOnly;
        return `${d}/${mo}/${y}`;
    }
    const dt = new Date(input);
    if (Number.isNaN(dt.getTime()))
        return input;
    const dd = String(dt.getDate()).padStart(2, '0');
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    return `${dd}/${mm}/${dt.getFullYear()}`;
}
const pad2 = (n) => String(n).padStart(2, '0');
const iso = (y, m, d) => `${y}-${pad2(m)}-${pad2(d)}`;
const monthName = (m) => HE_MONTHS[m - 1] ?? String(m);
/** "דצמבר 2026" for a 'YYYY-MM' month key. */
export function monthKeyLabel(month) {
    const [y, m] = month.split('-').map(Number);
    return `${monthName(m)} ${y}`;
}
/** Last day of 1-indexed month m in year y. */
function daysInMonth(y, m) {
    return new Date(Date.UTC(y, m, 0)).getUTCDate();
}
/** Shift a 1-indexed (year, month) by delta months. */
function addMonths(y, m, delta) {
    const zero = (y * 12 + (m - 1)) + delta;
    return { y: Math.floor(zero / 12), m: (zero % 12) + 1 };
}
/** "DD.MM" — compact range piece for the custom-period label. */
function dm(isoDate) {
    const [, m, d] = isoDate.split('-');
    return `${d}.${m}`;
}
/** Today as YYYY-MM-DD in Asia/Jerusalem — the business timezone for all date
 *  boundaries (guidelines 03 §15). Safe on both client and server. */
export function todayInJerusalem(now = new Date()) {
    // en-CA yields ISO-ish YYYY-MM-DD; the timeZone option does the conversion.
    return new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Asia/Jerusalem',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(now);
}
/**
 * Resolve a concrete [start_date, end_date] (inclusive) + label from the box's
 * pay-period config and the selected period.
 *
 * Custom (start day D) uses the END-MONTH convention: the period labeled month M
 * runs day D of month M-1 → day (D-1) of month M. e.g. D=25, M=May →
 * 25 Apr → 24 May (the "May" period). D is capped at 28 (PayPeriodConfigSchema)
 * so day D-1 ≥ 1 and day D always exists in the previous month — no end-of-month
 * edge cases. Flip to start-month by swapping the two month refs below.
 */
export function resolvePayPeriod(config, selection, todayISO) {
    if (selection.kind === 'custom') {
        return {
            start_date: selection.start_date,
            end_date: selection.end_date,
            label: `${dm(selection.start_date)}.${selection.start_date.slice(0, 4)}–${dm(selection.end_date)}.${selection.end_date.slice(0, 4)}`,
        };
    }
    let y;
    let m;
    if (selection.kind === 'month') {
        // Explicit calendar month chosen on the report.
        y = selection.year;
        m = selection.month;
    }
    else {
        const [ty, tm] = todayISO.split('-').map(Number);
        const delta = selection.kind === 'last_month' ? -1 : selection.kind === 'next_month' ? 1 : 0;
        ({ y, m } = addMonths(ty, tm, delta));
    }
    if (config.mode === 'calendar_month') {
        return {
            start_date: iso(y, m, 1),
            end_date: iso(y, m, daysInMonth(y, m)),
            label: `${monthName(m)} ${y}`,
        };
    }
    // custom — end-month convention
    const d = config.custom_start_day ?? 1;
    const prev = addMonths(y, m, -1);
    const start = iso(prev.y, prev.m, d);
    const end = iso(y, m, d - 1);
    return {
        start_date: start,
        end_date: end,
        label: `${monthName(m)} ${y} (${dm(start)}–${dm(end)})`,
    };
}
//# sourceMappingURL=pay-period.js.map